<template>
    <report
        :report="report"
        :versions="versions"
        :loading="loading"
        :refreshing="refreshing"
        export-url="/report/executive_dashboard/export"
        @reload="reload"
        @load-version="load_version"
    >
        <div class="lam-graph-container">
            <div class="uk-text-right uk-margin-bottom">
                <div class="uk-button-group lam-graph-filter">
                    <base-button
                        size="mini"
                        :class="{'uk-active': state.scope === 1}"
                        @click.prevent="set_state_value('scope', 1)"
                    >
                        1 year
                    </base-button>
                    <base-button
                        size="mini"
                        :class="{'uk-active': state.scope === 2}"
                        @click.prevent="set_state_value('scope', 2)"
                    >
                        2 years
                    </base-button>
                </div>
            </div>

            <cards
                id="executive-dashboard"
                :cards="cards"
                :selectable="false"
                :flex="true"
            >
                <template slot="card" slot-scope="props">
                    <div v-if="props.card.id === 'flipped'">
                        <div class="uk-text-muted uk-margin-bottom">
                            Of the proposals that went out in any given {{ unit }}, how many of those projects have since turned active.
                        </div>
                        <hit-rate-graph
                            :chart-data="hit_rate_data"
                            style="height: 300px;"
                        />
                    </div>
                    <div v-else-if="props.card.id === 'balance'">
                        <div class="uk-text-muted uk-margin-bottom">
                            In a given {{ unit }}, how many projects went active / how many proposals went out
                        </div>
                        <balance-graph
                            :chart-data="balance_data"
                            style="height: 300px;"
                        />
                    </div>
                    <div v-else-if="props.card.id === 'proposals_sent'">
                        <div class="uk-text-muted uk-margin-bottom">
                            Total proposals sent out in a given {{ unit }}. Dark blue equates to projects that have since gone active.
                        </div>
                        <proposals-sent-graph
                            :chart-data="proposals_sent_data"
                            style="height: 300px;"
                        />
                    </div>
                    <div v-else-if="props.card.id === 'proposals_fees'">
                        <div class="uk-text-muted uk-margin-bottom">
                            Total dollars sent out in proposals in a given {{ unit }}. Dark green equates to projects that have since gone active.
                        </div>
                        <proposal-fees-graph
                            :chart-data="proposal_fees_data"
                            :average="proposal_fees_avg"
                            style="height: 300px;"
                        />
                    </div>
                    <div v-else-if="props.card.id === 'activated_projects_by_count'">
                        <div class="uk-text-muted uk-margin-bottom">
                            Total quantity of projects turned active in a given {{ unit }}.
                        </div>
                        <actived-projects-graph
                            :chart-data="activated_projects_data('count')"
                            field="count"
                            style="height: 300px;"
                            @click="route_to_record"
                        />
                    </div>
                    <div v-else-if="props.card.id === 'activated_projects_by_fee'">
                        <div class="uk-text-muted uk-margin-bottom">
                            Total fees of projects turned active in a given {{ unit }}.
                        </div>
                        <actived-projects-graph
                            :chart-data="activated_projects_data('fee')"
                            field="fee"
                            style="height: 300px;"
                            @click="route_to_record"
                        />
                    </div>
                    <div v-else-if="props.card.id === 'active_phases_by_count'">
                        <div class="uk-text-muted uk-margin-bottom">
                            Number of projects in each phase. Current month shows real-time.
                        </div>
                        <active-phases-graph
                            :chart-data="active_phases_data('count')"
                            field="count"
                            style="height: 300px;"
                        />
                    </div>
                    <div v-else-if="props.card.id === 'active_phases_by_fee'">
                        <div class="uk-text-muted uk-margin-bottom">
                            Total fees projects in each phase. Current month shows real-time.
                        </div>
                        <active-phases-graph
                            :chart-data="active_phases_data('fee')"
                            field="fee"
                            style="height: 300px;"
                        />
                    </div>
                </template>
            </cards>
        </div>
    </report>
</template>

<script type="text/javascript">
import orderBy from "lodash/orderBy"

import is_report from "./IsReport"
import uses_color_palette from "../../mixins/UsesColorPalette"

import Cards from "../../ui/Cards"
import Report from "./Report.vue"
import BaseButton from "../../ui/BaseButton"
import HitRateGraph from "./ExecutiveDashboard/HitRateGraph.js"
import BalanceGraph from "./ExecutiveDashboard/BalanceGraph.js"
import ProposalFeesGraph from "./ExecutiveDashboard/ProposalFeesGraph.js"
import ProposalsSentGraph from "./ExecutiveDashboard/ProposalsSentGraph.js"
import ActivedProjectsGraph from "./ExecutiveDashboard/ActivedProjectsGraph.js"
import ActivePhasesGraph from "./ExecutiveDashboard/ActivePhasesGraph.js"

export default {
    mixins: [is_report, uses_color_palette],
    components: {
        Cards,
        Report,
        BaseButton,
        HitRateGraph,
        BalanceGraph,
        ProposalFeesGraph,
        ProposalsSentGraph,
        ActivedProjectsGraph,
        ActivePhasesGraph
    },
    methods: {
        report_data (report_dataset, data, data_callback, dataset_callback, raw_value_template = null) {
            if(this.report) {
                const by_quarters = (this.state.scope > 1)
                const to = this.$moment()
                const from = this.$moment(to).subtract(this.state.scope, "years")
                if(by_quarters) from.startOf("year")
                let range_start = this.$moment(from).startOf("month")
                while(range_start.isSameOrBefore(to)) {
                    const count = by_quarters ? 3 : 1
                    const range_end = this.$moment(range_start).add(count, "months") // .endOf("month")

                    if(by_quarters) {
                        const quarter = range_start.quarter()
                        if(quarter === 1) data.labels.push(`${range_start.format("YY")}-Q${quarter}`)
                        else data.labels.push(`Q${quarter}`)
                    } else {
                        const month = range_start.month()
                        if(!month) data.labels.push(`${range_start.format("YY")}-${range_start.format("MMM")}`)
                        else data.labels.push(`${range_start.format("MMM")}`)
                    }

                    const raw_value = raw_value_template ? { ...raw_value_template } : { prospective: 0, active: 0 }
                    Object.keys(report_dataset).forEach(date_string => {
                        const date = this.$moment(date_string)

                        if(date.isBetween(range_start, range_end, undefined, "[)")) {
                            data_callback(date_string, raw_value, range_start, range_end)
                        }
                    })

                    dataset_callback(raw_value, range_start, range_end)

                    range_start = this.$moment(range_end).startOf("month")
                }
            }
        },
        active_phases_data (field) {
            const data = {
                labels: [],
                datasets: [],
                raw_data: []
            }
            const report_dataset = this.report ? this.report.dataset.active_phases : {}
            let phases = []
            Object.values(report_dataset).forEach(date_dataset => {
                if(!Array.isArray(date_dataset)) {
                    Object.keys(date_dataset).forEach(phase => {
                        if(phases.indexOf(phase) < 0) phases.push(phase)
                    })
                }
            })
            phases = orderBy(phases, function (phase) {
                const standardized_phase = phase.toLowerCase()
                const index = ["sd", "dd", "cd", "ca", "incentives", "other"].indexOf(standardized_phase)
                if(index >= 0) return index
                return 1000
            }, "asc")
            /*
            phases.sort((phase_a, phase_b) => {
                if(phase_a === 'Other') return 1
                if(phase_b === 'Other') return -1
                return phase_a.localeCompare(phase_b)
            })
            */

            const raw_value_template = {}
            phases.forEach(phase => {
                const color = this.phase_color(phase)
                data.datasets.push({
                    label: phase,
                    backgroundColor: color,
                    borderColor: color,
                    borderWidth: 1,
                    categoryPercentage: 0.9,
                    barPercentage: 0.8,
                    data: []
                })
                raw_value_template[phase] = 0
            })

            this.report_data(
                report_dataset,
                data,
                (date_string, raw_value, range_start, range_end) => {
                    phases.forEach(phase => {
                        if(!!report_dataset[date_string][phase] && report_dataset[date_string][phase][field]) raw_value[phase] += report_dataset[date_string][phase][field]
                    })
                },
                (raw_value, range_start, range_end) => {
                    phases.forEach((phase, index) => {
                        if(!data.raw_data[data.datasets[index].data.length]) data.raw_data[data.datasets[index].data.length] = raw_value[phase]
                        else data.raw_data[data.datasets[index].data.length] += raw_value[phase]
                        data.datasets[index].data.push(raw_value[phase])
                    })
                },
                raw_value_template
            )

            return data
        },
        activated_projects_data (field) {
            const data = {
                labels: [],
                datasets: []
            }

            if(this.report) {
                const report_dataset = this.report.dataset.activated_projects
                const raw_value_template = {}
                const project_datasets = {}
                Object.values(report_dataset).forEach(date_data => {
                    Object.keys(date_data).forEach(project_id => {
                        project_id = parseInt(project_id)
                        const project_identifier = `P${project_id}`
                        if(!raw_value_template[project_identifier]) {
                            const project = this.entity_records("project").find(project => project.id === project_id)
                            if(project) {
                                raw_value_template[project_identifier] = 0
                                project_datasets[`${project.number}.${project.number_suffix}`] = ({
                                    label: this.nibnut_filter("nibnut.smart-truncate", [`${project.number}.${project.number_suffix} ${project.name}`, 30]),
                                    backgroundColor: project.hex_color,
                                    borderColor: "#ffffff",
                                    borderWidth: 1,
                                    categoryPercentage: 0.9,
                                    barPercentage: 0.8,
                                    data: [],
                                    project
                                })
                            }
                        }
                    })
                })
                this.report_data(
                    report_dataset,
                    data,
                    (date_string, raw_value, range_start, range_end) => {
                        Object.keys(raw_value).forEach(project_identifier => {
                            const project_id = parseInt(project_identifier.replace(/^P/, ""))
                            if(report_dataset[date_string][project_id]) raw_value[project_identifier] += (field === "fee") ? report_dataset[date_string][project_id] : 1
                        })
                    },
                    (raw_value, range_start, range_end) => {
                        Object.keys(raw_value).forEach((project_identifier, index) => {
                            const project_id = parseInt(project_identifier.replace(/^P/, ""))
                            const project = this.entity_records("project").find(project => project.id === project_id)
                            if(project) project_datasets[`${project.number}.${project.number_suffix}`].data.push(raw_value[project_identifier])
                        })
                    },
                    raw_value_template
                )
                data.datasets = orderBy(Object.values(project_datasets), dataset => `${dataset.project.number}.${dataset.project.number_suffix}`, "asc")
            }

            return data
        },
        zoom_category (category) {
            this.set_state_value("filter", category)
            this.load()
        }
    },
    computed: {
        fields () {
            return ["ns::project;fieldset::graph"]
        },
        cards () {
            const cards = [
                {
                    id: "flipped",
                    title: "Flipped (Hit Rate)",
                    classes: { "uk-width-small-1-1": true, "uk-width-large-1-2": true }
                },
                {
                    id: "balance",
                    title: "Balance",
                    classes: { "uk-width-small-1-1": true, "uk-width-large-1-2": true }
                },
                {
                    id: "proposals_sent",
                    title: "Proposals Sent",
                    classes: { "uk-width-small-1-1": true, "uk-width-large-1-2": true }
                },
                {
                    id: "proposals_fees",
                    title: "Proposal Fees",
                    classes: { "uk-width-small-1-1": true, "uk-width-large-1-2": true }
                },
                {
                    id: "activated_projects_by_count",
                    title: "Activated Projects (By Count)",
                    classes: { "uk-width-small-1-1": true, "uk-width-large-1-2": true }
                },
                {
                    id: "activated_projects_by_fee",
                    title: "Activated Projects (By Fee)",
                    classes: { "uk-width-small-1-1": true, "uk-width-large-1-2": true }
                },
                {
                    id: "active_phases_by_count",
                    title: "Active Phases (By Count)",
                    classes: { "uk-width-small-1-1": true, "uk-width-large-1-2": true }
                },
                {
                    id: "active_phases_by_fee",
                    title: "Active Phases (By Fee)",
                    classes: { "uk-width-small-1-1": true, "uk-width-large-1-2": true }
                }
            ]

            return cards
        },
        unit () {
            if(this.state.scope > 1) return "quarter"
            return "month"
        },
        hit_rate_data () {
            const data = {
                labels: [],
                datasets: [
                    {
                        backgroundColor: this.color_languished.background,
                        borderColor: this.color_languished.border,
                        borderWidth: 1,
                        categoryPercentage: 0.9,
                        barPercentage: 0.8,
                        data: []
                    }
                ],
                raw_data: []
            }

            if(this.report) {
                const report_dataset = this.report.dataset.hit_rate
                this.report_data(
                    report_dataset,
                    data,
                    (date_string, raw_value, range_start, range_end) => {
                        raw_value.prospective += report_dataset[date_string].prospective
                        raw_value.active += report_dataset[date_string].active
                    },
                    (raw_value, range_start, range_end) => {
                        data.datasets[0].data.push((raw_value.active / (raw_value.prospective + raw_value.active)) * 100)
                        data.raw_data.push(raw_value)
                    }
                )
            }

            return data
        },
        balance_data () {
            const data = {
                labels: [],
                datasets: [
                    {
                        backgroundColor: this.color_active.background,
                        borderColor: this.color_active.border,
                        borderWidth: 1,
                        categoryPercentage: 0.9,
                        barPercentage: 0.8,
                        data: []
                    }
                ],
                raw_data: []
            }

            if(this.report) {
                const report_dataset = this.report.dataset.balance
                this.report_data(
                    report_dataset,
                    data,
                    (date_string, raw_value, range_start, range_end) => {
                        raw_value.prospective += report_dataset[date_string].prospective
                        raw_value.active += report_dataset[date_string].active
                    },
                    (raw_value, range_start, range_end) => {
                        data.datasets[0].data.push((raw_value.active / raw_value.prospective) * 100)
                        data.raw_data.push(raw_value)
                    }
                )
            }

            return data
        },
        proposal_fees_data () {
            const data = {
                labels: [],
                datasets: [
                    {
                        label: "Active",
                        backgroundColor: this.color_pending.background,
                        borderColor: this.color_pending.border,
                        borderWidth: 1,
                        categoryPercentage: 0.9,
                        barPercentage: 0.8,
                        data: []
                    },
                    {
                        label: "Prospective",
                        backgroundColor: "#b2dc8f",
                        borderColor: "#b2dc8f",
                        borderWidth: 1,
                        categoryPercentage: 0.9,
                        barPercentage: 0.8,
                        data: []
                    }
                ],
                raw_data: []
            }

            if(this.report) {
                const report_dataset = this.report.dataset.proposal_fees
                this.report_data(
                    report_dataset,
                    data,
                    (date_string, raw_value, range_start, range_end) => {
                        raw_value.prospective += report_dataset[date_string].prospective
                        raw_value.active += report_dataset[date_string].active
                    },
                    (raw_value, range_start, range_end) => {
                        data.datasets[0].data.push(raw_value.active)
                        data.datasets[1].data.push(raw_value.prospective)
                        data.raw_data.push(raw_value)
                    }
                )
            }

            if(data.datasets[1].data.length) {
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.proposal_fees_avg = data.datasets[1].data.reduce((total, fee) => {
                    return total + fee
                }, 0) / data.datasets[1].data.length
            } else {
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.proposal_fees_avg = 0
            }

            return data
        },
        proposals_sent_data () {
            const data = {
                labels: [],
                datasets: [
                    {
                        label: "Active",
                        backgroundColor: this["0044ff"].background,
                        borderColor: this["0044ff"].border,
                        borderWidth: 1,
                        categoryPercentage: 0.9,
                        barPercentage: 0.8,
                        data: []
                    },
                    {
                        label: "Prospective",
                        backgroundColor: "#5196fd",
                        borderColor: "#5196fd",
                        borderWidth: 1,
                        categoryPercentage: 0.9,
                        barPercentage: 0.8,
                        data: []
                    }
                ],
                raw_data: []
            }

            if(this.report) {
                const report_dataset = this.report.dataset.proposals_sent
                this.report_data(
                    report_dataset,
                    data,
                    (date_string, raw_value, range_start, range_end) => {
                        raw_value.prospective += report_dataset[date_string].prospective
                        raw_value.active += report_dataset[date_string].active
                    },
                    (raw_value, range_start, range_end) => {
                        data.datasets[0].data.push(raw_value.active)
                        data.datasets[1].data.push(raw_value.prospective)
                        data.raw_data.push(raw_value)
                    }
                )
            }

            return data
        },
        category_data () {
            return this.report.dataset.active_categories
        }
    },
    data () {
        return {
            report_id: "executive_dashboard",

            default_state: {
                scope: 1, // years
                mode: "absolute",
                filter: "",
                user_id: 0,
                studio_id: 0,
                version_id: 0
            },

            proposal_fees_avg: 0
        }
    }
}
</script>
